// SC-2376 unified table-query contract — the FE-side mirror of the backend's
// `POST /catalogs/{id}/table` request/response. Shapes follow the *as-built*
// handoff (next-backend/issues/SC-2376/fe-handoff.md), which WINS over the
// SC-2356 design doc wherever they disagree. The thesis: one `TableQuery` is
// the table's single source of truth, and BOTH the user's deterministic
// controls AND the agent (Slice D) emit into it — so this is modeled to be
// produced by either editor.
//
// Slice status is baked into the comments below: Slices B (paging + filters),
// C (findings overlay), and F (per-row completeness + the band filter) are
// LIVE; `sort` (the composite-cursor slice, SC-2402) is now LIVE too — a non-null
// sort orders by a real column or the synthetic `_completeness` (SC-2409). The
// completeness ROLLUP (catalog-wide avg + band counts) rides Slice E facets.

import type { FindingStatus } from './index';

export type SortDir = 'asc' | 'desc';

// `field_value` operators (as-built). `in` ORs within `values`; `blank` /
// `not_blank` ignore `values`; ranges (gt/gte/lt/lte) are best-effort on string
// data server-side. Predicates AND across the filters list.
export type FilterOp =
  | 'eq'
  | 'neq'
  | 'in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'contains'
  | 'blank'
  | 'not_blank';

export interface FieldValueFilter {
  type: 'field_value';
  field: string;
  op: FilterOp;
  // OR within a single predicate. Omitted/ignored for `blank` / `not_blank`.
  values?: string[];
}

export interface IdentifierFilter {
  type: 'identifier';
  ids: string[];
}

export interface TextFilter {
  type: 'text';
  query: string;
}

// Entities that HAVE a user-facing finding. Defaults to OPEN findings
// (pending + acknowledged) when `status` is omitted; `field` scopes it to one
// column. `kind` semantics (has-suggestion vs flag-for-review) aren't pinned in
// the as-built doc yet — typed opaque so we don't over-constrain the wire.
export interface FindingFilter {
  type: 'finding';
  field?: string;
  status?: FindingStatus[];
  kind?: string;
}

// Presentation bands (a shared constant, not stored per row): Complete ≥85 ·
// Partial 60–84 · Sparse <60.
export type CompletenessBand = 'complete' | 'partial' | 'sparse';

// ✅ Slice F LIVE — ranges the server-computed per-row completeness % into a
// band (Complete ≥85 · Partial 60–84 · Sparse <60).
export interface CompletenessFilter {
  type: 'completeness';
  band: CompletenessBand;
}

// ✅ SC-3104 — entities that REACH the named channels. "Reaches", not "is tagged
// with", and the difference is load-bearing: a destination scoped `all_entities`
// receives every row of an in-scope catalog without any of them carrying its id,
// so the server compiles this to match-all for such a channel. Compiling it as a
// bare membership test would answer "no rows" for a channel that in fact takes
// the whole catalog — an empty CSV handed to a client as their assortment.
//
// That resolution lives entirely server-side, so the FE sends this predicate for
// EVERY channel with no client-side special-casing. Don't reintroduce any here.
//
// `slugs` is OR'd (an entity in ANY of them matches) and must be non-empty — an
// empty list is a 422, not "no filter". Every slug must name a real destination;
// an unknown one is an error rather than a silently narrower result.
export interface DestinationFilter {
  type: 'destination';
  slugs: string[];
}

export type TableFilter =
  | FieldValueFilter
  | IdentifierFilter
  | TextFilter
  | FindingFilter
  | CompletenessFilter
  | DestinationFilter;

// A field name OR a synthetic key — `_completeness` | `_finding_count` |
// `_severity` — resolved server-side. Typed as `string` (the synthetic keys
// would collapse a union into `string` anyway); the synthetic set is documented
// here rather than enforced at the type level.
export type SortKey = string;

export interface Sort {
  key: SortKey;
  dir: SortDir;
}

export interface TableQuery {
  // AND-combined across the list.
  filters: TableFilter[];
  // The projection. `[]` ⇒ the catalog's preview_fields, echoed back in
  // `TablePage.columns`. Dotted column names can't be projected and are dropped.
  columns: string[];
  // SC-2402 LIVE: orders by a real column or the synthetic `_completeness`;
  // null ⇒ the default stable `_id` order.
  sort: Sort | null;
  // Opaque; pass the prior page's `next_cursor`. null ⇒ first page.
  cursor: string | null;
  // 1..200, default 50.
  limit: number;
}

// SC-3149 — the file formats `POST /table/export` serves (`?format=`). A query
// PARAMETER, never a `TableQuery` field: the export posts the grid's query
// unchanged. CSV is the backend default; xlsx types every cell as text, so a
// value opens byte-exact in Excel and is never evaluated as a formula.
export type ExportFormat = 'csv' | 'xlsx';

// --- Response: TablePage ----------------------------------------------------

// `entity_id` is the catalog identity value (the SKU), or null for un-profiled
// catalogs with no identity field. `data` carries ONLY the resolved `columns`
// — never the full 4k-key dict (that's the Slice G single-entity deep-read).
export interface TableRow {
  // SC-3106 — the row's Mongo `_id`, the ONLY address a cell edit takes
  // (`entity_id` is the identity value: null on an un-keyed catalog and able to
  // repeat). Required on the SC-3106 backend; optional here because a table
  // served by an older API omits it, and a row without it renders read-only
  // rather than sending `row_id: undefined`.
  id?: string;
  entity_id: string | null;
  data: Record<string, unknown>;
  // ✅ Slice F — server-computed completeness, int 0–100 (populated = value not
  // null/undefined/''; `0` and `false` count). Drives the Health column + drawer
  // chip without a client compute over the projected (partial) row.
  completeness: number;
}

// Cell-level overlay kind → prototype render state: `fill` = ghosted
// suggestion, `replace`/`remove` = flagged cell, `flag_for_review` = review
// flag with no ghost value, `enrich` (SC-2459) = a bounded enrichment whose value
// isn't known until apply (no `suggested_value`) — render the cell as "being
// enriched" and pull the plan from the finding's proposal (joined by `finding_id`).
// `transform` (SC-3468) = a bounded find-and-replace / rule on this row; no
// `suggested_value` (computed at apply) — its rules ride on `Highlight.proposals`.
export type HighlightKind = 'fill' | 'replace' | 'remove' | 'flag_for_review' | 'enrich' | 'transform';

// ⚠️ As-built serializes these UPPERCASE — the SC-2356 design doc wrote them
// lowercase; the handoff (as-built) wins. Render confidence as a BAND, not a %.
// Named `*Band` to avoid colliding with the lowercase `Confidence` profile type.
export type ConfidenceBand = 'HIGH' | 'MEDIUM' | 'LOW';
export type FindingSeverityBand = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface Highlight {
  entity_id: string;
  field: string;
  kind: HighlightKind;
  // The finding's urgency (a distinct axis from `confidence`): CRITICAL…LOW, the
  // NAME not an int. Colors the flagged cell.
  severity: FindingSeverityBand;
  // Ghost value for `fill` / `replace`; null for `remove` / `flag_for_review`.
  suggested_value: string | null;
  // How sure the AI is the suggestion is correct — nullable (some findings aren't
  // AI-scored). A different axis from `severity`; don't conflate.
  confidence: ConfidenceBand | null;
  // Ranked candidate slate (SC-2391), best-first with candidates[0] ===
  // suggested_value; null for a confident single-value suggestion. The BE has
  // always sent this — the FE type just never declared it (SC-2798).
  candidate_values: string[] | null;
  // A value cell's value when the finding was made (the op's `before`; null = it was
  // empty), the diff's "before" (SC-3501). Absent from an older BE → the live cell.
  original_value?: string | null;
  // Tooltip "why" text.
  rationale: string | null;
  // Provenance + apply target.
  finding_id: string;
  // A `transform` cell's own rules, in apply order (SC-3468). One finding can hold
  // several transforms on one field — for different rows, or several for one row — so
  // the finding-level `proposals` can't say which apply here. Absent/null for every
  // other kind (and from an older BE).
  proposals?: FindingProposal[] | null;
}

// Authoritative CTA for a finding (SC-2459): `review` → "needs your decision",
// NO one-click apply, even when `has_suggestions` is also true (a finding can carry
// BOTH a changeset and a human_review — review dominates). `apply` → the suggestion
// track. Render the CTA from THIS, not from the two booleans (which let an
// un-appliable enrich win a confident "Apply fix").
export type FindingCta = 'apply' | 'review';

// One op-aware "becomes" on a finding (SC-2459), one per resolving op. `descriptor`
// is the human BECOMES line — ALWAYS safe to render as-is (e.g. "Re-fetch from
// manufacturer CDN: correct image URL…", "Rename → Size"). The structured fields are
// there for richer rendering than the one-liner. EMPTY for a review-only finding —
// its content is `review_question`, not a proposal.
export interface FindingProposal {
  field: string;
  verb: 'fill' | 'replace' | 'remove' | 'enrich' | 'transform' | 'schema';
  descriptor: string;
  // value ops: the single proposed value (per-field collapse).
  proposed_value: string | null;
  // value ops: ranked slate (best-first) when present.
  candidate_values: string[] | null;
  // enrich: what to look up · where it comes from.
  enrich_what: string | null;
  enrich_source_hint: string | null;
  // transform: the rule name. transform/schema: raw params.
  transform_rule: string | null;
  params: Record<string, unknown> | null;
}

// A finding as referenced from a row/column rollup — enough to render a list
// item (summary + severity + intent) and to know whether it's actionable
// (`requires_review` → human decision; `has_suggestions` → an applyable change).
// These two booleans are FINDING-level (true if the finding has a human_review /
// changeset anywhere); the row/column `review_count` / `suggestion_count` are the
// narrower PER-SCOPE actionable counts (see RowFlag).
//
// SC-2459 fattened the per-finding payload so the op-aware drawer card can render
// CURRENT/BECOMES + the "why" for enrich / transform / review findings (not just
// `ValueMutation`s). The new fields are OPTIONAL for back-compat — a pre-SC-2459
// page (or an older fixture) omits them and the card falls back to its prior
// behavior (CTA from `has_suggestions`, no proposal becomes).
export interface FindingRef {
  finding_id: string;
  summary: string;
  severity: FindingSeverityBand;
  // The finding's intent (e.g. deduplicate | normalize | fill | correct). Opaque
  // string — the column rollup's `kinds` is the distinct set of these.
  intent: string;
  requires_review: boolean;
  has_suggestions: boolean;
  // --- SC-2459 (op-aware payload) ---
  // Authoritative CTA — render the card's action from this, NOT the two booleans.
  cta?: FindingCta;
  // The "why" — present for catalog-/column-scope findings that carry no per-cell
  // Highlight (those used to have no rationale on the card).
  rationale?: string | null;
  // AI confidence, rendered as a BAND.
  confidence?: ConfidenceBand | null;
  // Grounding / evidence columns to show beside the change.
  reference_fields?: string[];
  // Columns the finding touches — even with no cell Highlight (gives a field-less
  // enrich / review a per-field home + a CURRENT value).
  fields?: string[];
  // The decision a person must make (= `human_review.question`).
  review_question?: string | null;
  // SC-3249 plain-language layers (same as `FindingResponse`): "why it matters", and
  // on a decision-needed finding what we'd choose. Optional like the SC-2459 fields:
  // absent on a BE that predates them, null on findings minted before them.
  impact?: string | null;
  recommendation?: string | null;
  // Op-aware BECOMES, one per resolving op; empty for a review-only finding.
  proposals?: FindingProposal[];
  // SC-3257 — the whole-finding apply would write (same rule as
  // `FindingResponse.auto_applicable`). Independent of `cta`: a split finding is
  // `cta: "apply"` + `auto_applicable: false` — describe it, offer no Apply.
  auto_applicable: boolean;
}

// Per-page entity finding rollup — shows even when the flagged column is HIDDEN
// (the fresh / minimal-view case), so a row can signal "N issues" without the
// offending column being visible.
//
// `review_count` / `suggestion_count` are PER-ENTITY actionable counts, NOT a
// re-count of `findings`: `suggestion_count` = findings with a concrete cell op
// (ValueMutation) for THIS entity; `review_count` = findings whose human_review
// names this entity. So a systematic finding (e.g. a column-wide normalize) can
// be in `findings` / `finding_count` yet add to neither count — it surfaces as a
// ColumnFlag instead. The two counts therefore can sum to less than
// `finding_count`; render them as the prototype's ✦ (suggestion) / ⚑ (review)
// badges, and fall back to `finding_count` when both are 0 (systematic-only).
export interface RowFlag {
  entity_id: string;
  finding_count: number;
  max_severity: FindingSeverityBand;
  review_count: number;
  suggestion_count: number;
  findings: FindingRef[];
}

// Systematic (affects_all) issues on a visible column. `kinds` is the set of
// finding INTENTS (normalize | fill | correct | …) — deliberately DISTINCT from
// `HighlightKind` (the per-cell action verbs fill/replace/remove/enrich/transform/flag_for_review).
export interface ColumnFlag {
  field: string;
  finding_count: number;
  kinds: string[];
  findings: FindingRef[];
  suggestion_count: number;
  review_count: number;
}

export interface TablePage {
  // This page only.
  rows: TableRow[];
  // Exact count for the filtered set.
  total_rows: number;
  // base64; null on the last page.
  next_cursor: string | null;
  // Resolved / echoed (what was actually projected).
  columns: string[];
  // Cells on THIS page × visible columns.
  highlights: Highlight[];
  // Per-page entity finding rollup.
  row_flags: RowFlag[];
  // Systematic issues on the visible columns.
  column_flags: ColumnFlag[];
}

// --- Blank-query helpers ----------------------------------------------------
// Runtime helpers kept beside the type (not embedded IN `TableQuery`, so the
// shape stays pure) and at this leaf layer so BOTH the table hook and the
// table-view store build a blank query without an inverted store → component
// import.

// BE default page size (1..200).
export const DEFAULT_TABLE_LIMIT = 50;

/**
 * A blank `TableQuery` — no filters, catalog preview_fields projection (`[]`),
 * `sort: null` (the default `_id` order), first page. Spread
 * `overrides` to set columns / filters / cursor / limit. Pure + exported so the
 * query model is unit-testable without a react-query harness.
 */
export function emptyTableQuery(overrides: Partial<TableQuery> = {}): TableQuery {
  return {
    filters: [],
    columns: [],
    sort: null,
    cursor: null,
    limit: DEFAULT_TABLE_LIMIT,
    ...overrides,
  };
}

// --- Agent producer: ViewDirective (SC-2376 Slice D) ------------------------
// The agent's `get_table_slice` tool emits this over the chat SSE stream (the
// `tool_use_done` event's `content`). It's a REQUEST — a `TableQuery` to apply
// — NOT a result: it carries no rows and no finding ids (which findings are in
// view come back in the `/table` overlay, resolved against the full query).
// `query.filters` can 422 at POST time (agent field names aren't pre-validated).

// `replace` swaps the whole view to `query`; `refine` ANDs `query.filters` into
// the view the user is currently looking at.
export type ViewMode = 'replace' | 'refine';

export interface ViewDirective {
  query: TableQuery;
  // User-facing caption to show alongside the re-rendered table.
  narration: string;
  mode: ViewMode;
}

/**
 * Structural guard for a `get_table_slice` tool result's `content`. The SSE
 * stream forwards tool output as `unknown`; this verifies enough to apply the
 * directive (`query.filters` / `query.columns` arrays, a known `mode`, a string
 * `narration`) and fails CLOSED if the BE shape drifts. Individual filter
 * elements aren't validated — a bad filter surfaces as a 422 from POST /table,
 * which the table's normal error path handles.
 */
export function isViewDirective(content: unknown): content is ViewDirective {
  if (!content || typeof content !== 'object') return false;
  const c = content as Record<string, unknown>;
  const query = c.query;
  if (!query || typeof query !== 'object') return false;
  const q = query as Record<string, unknown>;
  return (
    Array.isArray(q.filters) &&
    Array.isArray(q.columns) &&
    (c.mode === 'replace' || c.mode === 'refine') &&
    typeof c.narration === 'string'
  );
}

// --- SC-3106 / SC-3113 — edit one cell ---------------------------------------

/**
 * `POST /catalogs/{id}/cell-edits`. All four keys are required on the wire.
 * `before` is the value the user SAW (`null` = it looked blank) and is what makes
 * the write grounded: the server refuses with a 409 if the cell no longer holds
 * it. `""` and `null` mean the same thing server-side (both are blank).
 */
export interface CellEditRequest {
  row_id: string;
  field: string;
  before: string | null;
  after: string | null;
}

export interface CellEditResponse {
  /** Names this edit in the change history (the handle a future undo takes). */
  gesture_id: string;
  row_id: string;
  field: string;
  before: string | null;
  after: string | null;
  /** The row's new 0–100 score — same meaning as `TableRow.completeness`. */
  completeness: number;
}
